import { normalize } from "normalizr";
import React from "react";
import { reset } from "redux-form";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    ARTICLE_ADD_EXPERIENCE_SUBMIT,
    ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED,
    ArticleAddExperienceSubmitAction,
    ArticleAddExperienceSubmitUnauthenticatedAction,
    articleAddExperienceApi,
} from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.actions";
import { ARTICLE_ADD_EXPERIENCE_FORM_NAME } from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.constants";
import { XHRAction, XHRFormError } from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { getLogger } from "@/mk2/logger";
import { checkIfUserIsLoggedIn } from "@/mk2/sagas/auth";
import { HistoryAction } from "@/mk2/services/browserHistory";
import { WIKI_EXPERIENCE_SENT_TOAST } from "@/mk/autogenerated/translation";
import {
    pending_action_create_url,
    wiki_api_add_experience_url,
    wiki_api_add_experience_with_undef_article_url,
} from "@/mk/urls/functions";

const logger = getLogger("wiki/ArticleAddExperience.sagas");

function* submitExperienceSaga({
    categorySlug,
    articleSlug,
    values,
    gotoUrl,
    xhr,
    location,
    routeNavigate,
}: ArticleAddExperienceSubmitAction & XHRAction & HistoryAction) {
    yield put(articleAddExperienceApi.request(articleSlug, categorySlug));
    try {
        const response =
            categorySlug && articleSlug
                ? yield call(() =>
                      xhr.post(wiki_api_add_experience_url(articleSlug), {
                          values,
                      }),
                  )
                : yield call(() =>
                      xhr.post(wiki_api_add_experience_with_undef_article_url(), { values }),
                  );
        const normalizedReponse = normalize(response.body, {});
        yield handleXHRFormSuccessSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            routeNavigate,
            gotoUrl,
            WIKI_EXPERIENCE_SENT_TOAST,
        );
        yield put(articleAddExperienceApi.success(articleSlug, categorySlug, normalizedReponse));
    } catch (error) {
        yield handleXHRFormErrorSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            error as XHRFormError,
            logger,
        );
        yield put(articleAddExperienceApi.failure(articleSlug, categorySlug, error));
    }
}

function* submitExperienceUnauthenticatedSaga({
    articleSlug,
    categorySlug,
    values,
    xhr,
    location,
    routeNavigate,
}: ArticleAddExperienceSubmitUnauthenticatedAction & XHRAction & HistoryAction) {
    try {
        const response = yield call(() =>
            xhr.post(pending_action_create_url(), {
                actionType: "wiki_experience",
                payload: {
                    articleSlug: articleSlug || null,
                    categorySlug: categorySlug || null,
                    title: values.title,
                    bodyText: values.text,
                    rating: values.rating || null,
                    positives: values.positives || null,
                    negatives: values.negatives || null,
                    articleProposal: values.articleProposal || null,
                },
            }),
        );
        const token = response.body.token;
        yield put(reset(ARTICLE_ADD_EXPERIENCE_FORM_NAME));
        const publishUrl = `/pending-action/${token}/publish/`;
        yield call(checkIfUserIsLoggedIn, routeNavigate, location, publishUrl);
    } catch (error) {
        yield handleXHRFormErrorSaga(
            ARTICLE_ADD_EXPERIENCE_FORM_NAME,
            error as XHRFormError,
            logger,
        );
    }
}

export default function* root() {
    yield all([
        takeLatest(ARTICLE_ADD_EXPERIENCE_SUBMIT, submitExperienceSaga),
        takeLatest(
            ARTICLE_ADD_EXPERIENCE_SUBMIT_UNAUTHENTICATED,
            submitExperienceUnauthenticatedSaga,
        ),
    ]);
}
